/**
 * 异步资源加载 composable：inline error + 不重复 toast
 * API 失败时由 apiClient / runApiBatch presenter 负责 toast；本 composable 仅维护页面级 error ref
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { toL1Error, type L1Error } from '@/shared/api/l1-error'
import {
  formatApiErrorMessage,
  pickPrimaryError,
} from '@/shared/api/l1-error-presenter'
import {
  runApiBatch,
  type ApiBatchCall,
  type RunApiBatchOptions,
} from '@/shared/api/use-api-batch'

export interface UseAsyncResourceReturn<T> {
  data: Ref<T | null>
  loading: Ref<boolean>
  /** 结构化 L1 错误，供 inline Alert / 表单联动 */
  error: Ref<L1Error | null>
  /** 已 i18n 的展示文案 */
  errorMessage: ComputedRef<string>
  /** 执行单次加载；失败时写入 error，不 message.error */
  run: (loader: () => Promise<T>) => Promise<T | null>
  clearError: () => void
}

export function useAsyncResource<T>(): UseAsyncResourceReturn<T> {
  const data = ref<T | null>(null) as Ref<T | null>
  const loading = ref(false)
  const error = ref<L1Error | null>(null)

  const errorMessage = computed(() =>
    error.value ? formatApiErrorMessage(error.value) : '',
  )

  async function run(loader: () => Promise<T>): Promise<T | null> {
    loading.value = true
    error.value = null
    try {
      const result = await loader()
      data.value = result
      return result
    } catch (e) {
      const l1Err = toL1Error(e)
      error.value = l1Err
      console.error('useAsyncResource:', l1Err)
      return null
    } finally {
      loading.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  return {
    data,
    loading,
    error,
    errorMessage,
    run,
    clearError,
  }
}

/**
 * 并行 batch 加载：toast 由 runApiBatch 汇总；返回主错误供页面 inline Alert
 */
export async function runAsyncBatchWithError<T>(
  calls: Array<ApiBatchCall<T>>,
  options?: RunApiBatchOptions,
): Promise<{ results: PromiseSettledResult<T>[]; primaryError: L1Error | null }> {
  const results = await runApiBatch(calls, options)
  const rejected = results
    .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    .map((r) => toL1Error(r.reason))

  const primaryError = rejected.length > 0 ? pickPrimaryError(rejected) ?? null : null
  return { results, primaryError }
}
